import { useEffect, useRef, useState } from "react";
import { renderGreyThumb } from "./greyThumb.js";

/** How many grey thumbnails are kept: two pages of the index, where one page of 20 runs can
 *  hold 140 views. Each is a small `data:` URL. */
export const MAX_GREY_THUMBS = 300;

/**
 * Grey thumbnails made this session, by job: a signed link changes, the image does not. The
 * least recently used one leaves when the limit is reached. They are `data:` URLs, so there is
 * nothing to revoke.
 */
const made = new Map<string, string>();
export const greyThumbs = {
  get(key: string): string | undefined {
    const data = made.get(key);
    if (data !== undefined) {
      made.delete(key);
      made.set(key, data);
    }
    return data;
  },
  set(key: string, data: string): void {
    made.delete(key);
    made.set(key, data);
    if (made.size > MAX_GREY_THUMBS) made.delete(made.keys().next().value!);
  },
  get size(): number {
    return made.size;
  },
};

export const resetGreyThumbsForTest = () => made.clear();

/**
 * The grey-guide thumbnail of the image at `url`, or `undefined` until it is ready — the row
 * shows its placeholder meanwhile, never the magenta image. `key` names the job's result; a
 * missing `url` asks for nothing. `onError` reports a link that did not load.
 */
export function useGreyThumb(key: string, url: string | null | undefined, onError?: () => void): string | undefined {
  const [thumb, setThumb] = useState(() => greyThumbs.get(key));
  const failed = useRef(onError);
  failed.current = onError;
  useEffect(() => {
    const kept = greyThumbs.get(key);
    setThumb(kept);
    if (!url || kept !== undefined) return;
    let live = true;
    renderGreyThumb(url).then(
      (data) => {
        greyThumbs.set(key, data);
        if (live) setThumb(data);
      },
      () => { if (live) failed.current?.(); },
    );
    return () => { live = false; };
  }, [key, url]);
  return url ? thumb : undefined;
}
